import { useState, useEffect } from "react";
import { BuyerType } from "../../../../../constants/enums";
import {
  GetOrganizationAction,
  GetResourcesAction,
  GetServicesAction,
  GetBookingPageSettingsAction,
  GetSubscriptionAction,
} from "../../../../../store/action";
import {
  SubscriptionPlan,
  SELECTED_PLAN_KEY,
} from "../../../../../constants/subscription";
import { toMinutes } from "../utils/serviceHelpers";

/**
 * The plan to start the wizard on: whatever the visitor picked on the Pricing
 * page (stashed at SELECTED_PLAN_KEY), else the free Starter tier.
 */
const initialPlan = () => {
  const stored = parseInt(localStorage.getItem(SELECTED_PLAN_KEY), 10);
  return Number.isInteger(stored) ? stored : SubscriptionPlan.STARTER;
};

/**
 * Booking-page settings the wizard does not edit but must send back.
 *
 * `SaveBookingPageSettingsAction` posts the whole settings object, and a key
 * left out arrives as null and clears its column. The wizard edits a short
 * subset, so everything else it loaded rides along untouched.
 */
const UNEDITED_PAGE_FIELDS = [
  "aboutText",
  "ctaButtonText",
  "contactPhone",
  "contactEmail",
  "openingHours",
  "facebookUrl",
  "instagramUrl",
];

/**
 * Custom hook for managing wizard form state and data loading.
 *
 * Which step is on screen is the URL's business (see wizardSteps.js); this
 * hook only reports how far the organization had got — `resumeStep`, from
 * Organization.SetupStep — so a reload, another device or a bookmark lands on
 * the right page.
 */
export const useWizardForm = () => {
  const [formData, setFormData] = useState({
    // Step 2: Add Details
    businessName: "",
    // The booking page's address, which the server assigns when step 2
    // creates the organization; empty before that.
    slug: "",
    companyName: "",
    buyerType: String(BuyerType.COMPANY),
    registrationCode: "",
    phoneNumber: "",
    phoneCountryCode: "ro",
    county: "",
    city: "",
    address: "",

    // The plan, chosen from the top bar on any step. Before the organization
    // exists it rides on the create request; after, the top bar changes the
    // subscription directly.
    plan: initialPlan(),

    // Step 3: Resources
    resources: [],

    // Step 4: Services
    services: [],

    // Step 5: Booking Page
    bookingTitle: "",
    heroDescription: "",
    streetAddress: "",
    bookingCity: "",
    logoUrl: "",
    headerImageUrl: "",
    primaryColor: "#5c9f24",
    secondaryColor: "#1a1d19",
    pageExtras: {},
  });

  // The step to resume on, and whether there is an organization at all. The
  // server is the single source of truth for both.
  const [resumeStep, setResumeStep] = useState(1);
  const [organizationExists, setOrganizationExists] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadWizardData();
  }, []);

  const loadWizardData = async () => {
    try {
      // Always fetch fresh from the server — there is no local draft.
      const orgData = await GetOrganizationAction();
      if (orgData) {
        setOrganizationExists(true);
        // An organization existing at all means step 2 was already saved, so
        // step 2 is the floor if the server somehow didn't send a step back.
        setResumeStep(
          Number.isInteger(orgData.setupStep) ? Math.max(orgData.setupStep, 2) : 2,
        );

        setFormData((prev) => ({
          ...prev,
          businessName: orgData.businessName || "",
          slug: orgData.slug || "",
          companyName: orgData.companyName || "",
          buyerType: String(orgData.buyerType ?? BuyerType.COMPANY),
          registrationCode: orgData.registrationCode || "",
          phoneNumber: orgData.phoneNumber || "",
          phoneCountryCode: orgData.phoneCountryCode || "ro",
          county: orgData.county || "",
          city: orgData.city || "",
          address: orgData.address || "",
        }));

        // The chosen plan lives on the Subscription, not the Organization
        // row, so it isn't in orgData above. Restore it too — otherwise a
        // resume on another browser falls back to initialPlan()'s Starter
        // regardless of what was actually selected, which then wrongly caps
        // how many resources step 3 will allow adding.
        try {
          const usage = await GetSubscriptionAction();
          if (usage && Number.isInteger(usage.plan)) {
            setFormData((prev) => ({ ...prev, plan: usage.plan }));
          }
        } catch (error) {
          console.log("No subscription found yet");
        }

        try {
          const resourcesData = await GetResourcesAction();
          if (resourcesData && resourcesData.length > 0) {
            const mappedResources = resourcesData.map((r) => ({
              id: r.id,
              type: r.type,
              name: r.name,
              email: r.email || "",
              userId: r.userId ?? null,
              scheduleMode: r.scheduleMode,
              bookingAdvanceDays: r.bookingAdvanceDays,
              workingDays: r.workingDays,
              workingHours: r.workingHours,
              useAdvancedSchedule: r.useAdvancedSchedule,
              advancedSchedule: r.advancedSchedule,
              monthlySchedule: r.monthlySchedule,
            }));
            setFormData((prev) => ({ ...prev, resources: mappedResources }));
          }
        } catch (error) {
          console.log("No resources found yet");
        }

        try {
          const servicesData = await GetServicesAction();
          if (servicesData && servicesData.length > 0) {
            const mappedServices = servicesData.map((s) => ({
              id: s.id,
              name: s.name,
              duration: toMinutes(s.duration, s.durationUnit),
              durationUnit: "Minutes",
            }));
            setFormData((prev) => ({ ...prev, services: mappedServices }));
          }
        } catch (error) {
          console.log("No services found yet");
        }

        try {
          const settingsData = await GetBookingPageSettingsAction();
          if (settingsData) {
            setFormData((prev) => ({
              ...prev,
              bookingTitle: settingsData.bookingTitle || "",
              heroDescription: settingsData.heroDescription || "",
              streetAddress: settingsData.streetAddress || "",
              bookingCity: settingsData.bookingCity || "",
              logoUrl: settingsData.logoUrl || "",
              headerImageUrl: settingsData.headerImageUrl || "",
              primaryColor: settingsData.primaryColor || "#5c9f24",
              secondaryColor: settingsData.secondaryColor || "#1a1d19",
              pageExtras: Object.fromEntries(
                UNEDITED_PAGE_FIELDS.map((field) => [
                  field,
                  settingsData[field] || "",
                ]),
              ),
            }));
          }
        } catch (error) {
          console.log("No booking page settings found yet");
        }
      }
    } catch (error) {
      // No organization yet — first run, so the wizard starts at Welcome.
      console.log("No organization data found yet, starting fresh");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Drop this field's red mark the moment it is touched. The marks only
    // appear on Continue, so one that survives the fix it asked for is still
    // claiming something is wrong after it has been put right — and a form that
    // does that teaches people to stop reading its marks. Also covers the ANAF
    // lookup, which fills four fields through this same handler.
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const { [name]: _fixed, ...rest } = prev;
      return rest;
    });
  };

  return {
    formData,
    setFormData,
    resumeStep,
    organizationExists,
    setOrganizationExists,
    isLoading,
    errors,
    setErrors,
    handleInputChange,
  };
};
